import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Flex, Text, LoadingOrbit, Warning, DrawerTrigger, Drawer, Button, Page, PageHeader, Download, useToast, DocsAnnotation, EmptyState, EmptyStateScanGraphic, type SelectOption } from '@chatsdet/ui';
import type { RowSelectionState } from '@tanstack/react-table';
import type { IssueSeverity, IssueStatus } from '@chatsdet/types/issues';
import { API_URL } from '../../config';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useIssuesV2Query, type IssueSortField, type SortOrder } from '../../hooks/useIssuesV2';
import { useOrgCredentials } from '../../hooks/usePropertyMetadata';
import type { IssueSummary as IssueSummaryV2 } from '@chatsdet/types/issues';
import { IssuesTableV2 } from '../components/issues/IssuesTableV2';
import { IssueDrawerV2 } from '../components/issues/IssueDrawerV2';
import { IssuesFilters } from '../components/issues/IssuesFilters';
import { IssuesFilterBar } from '../components/issues/IssuesFilterBar';
import type { DateRangeValue } from '../../utils/dateRange';
import { toInclusiveIsoRange, parseIsoToDateRange } from '../../utils/dateRange';
import { useBulkArchiveIssues } from '../../hooks/useBulkArchiveIssues';
import { useBulkReproVerify } from '../../hooks/useBulkReproVerify';
import { useIssueCategoriesQuery } from '../../hooks/useIssueCategories';
import { useTagsQuery } from '../../hooks/useTags';
import { Copy } from '@phosphor-icons/react';
import { buildTriagePrompt } from '../components/issues/triagePrompt';

const PAGE_SIZE = 25;
const DEFAULT_STATUS_FILTER: IssueStatus[] = ['open'];

type PropertyItem = {
  id: string;
  name: string;
};

type IssuesFilterSnapshot = {
  severity?: IssueSeverity;
  propertyId?: string;
  statuses?: IssueStatus[];
  search?: string;
  lastSeenStart?: string;
  lastSeenEnd?: string;
  categoryIds?: string[];
  credentialIds?: string[];
  tagIds?: string[];
  includeArchived?: boolean;
};

type IssuesSearchParams = {
  issueId?: string;
  page: number;
  pageSize: number;
  severity?: IssueSeverity | 'all';
  propertyId?: string | 'all';
  statuses?: IssueStatus[];
  search?: string;
  lastSeenStart?: string;
  lastSeenEnd?: string;
  categoryIds?: string[];
  credentialIds?: string[];
  tagIds?: string[];
  includeArchived?: boolean;
  sortBy?: IssueSortField;
  sortOrder?: SortOrder;
};

export function CurrentIssuesPage() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/issues' }) as IssuesSearchParams;

  const updateSearch = React.useCallback(
    (updates: Partial<IssuesSearchParams>) =>
      navigate({
        to: '/issues',
        search: (prev: Record<string, unknown>) => {
          const prevParams = prev as IssuesSearchParams;
          const merged = { ...prevParams, ...updates };

          const next: IssuesSearchParams = {
            ...merged,
            page: Math.max(merged.page ?? 1, 1),
            pageSize: Math.max(merged.pageSize ?? PAGE_SIZE, 1),
            severity: (merged.severity ?? 'all') as typeof merged.severity,
            propertyId: (merged.propertyId ?? 'all') as typeof merged.propertyId,
            statuses: merged.statuses === undefined ? DEFAULT_STATUS_FILTER : merged.statuses,
          };

          if (!next.search) delete next.search;
          if (!next.lastSeenStart || !next.lastSeenEnd) {
            delete next.lastSeenStart;
            delete next.lastSeenEnd;
          }
          if (!next.categoryIds || next.categoryIds.length === 0) delete next.categoryIds;
          if (!next.credentialIds || next.credentialIds.length === 0) delete next.credentialIds;
          if (!next.tagIds || next.tagIds.length === 0) delete next.tagIds;
          if (!next.includeArchived) delete next.includeArchived;
          if (!next.issueId) delete next.issueId;
          if (!next.sortBy) delete next.sortBy;
          if (!next.sortOrder) delete next.sortOrder;

          return next;
        },
      }),
    [navigate]
  );

  const severityFilter = searchParams.severity ?? 'all';
  const propertyFilter = searchParams.propertyId ?? 'all';
  const statusFilter = searchParams.statuses ?? DEFAULT_STATUS_FILTER;
  const categoryFilter = searchParams.categoryIds ?? [];
  const credentialFilter = searchParams.credentialIds ?? [];
  const tagFilter = searchParams.tagIds ?? [];
  const showArchived = searchParams.includeArchived === true;
  const page = searchParams.page ?? 1;
  const pageSize = searchParams.pageSize ?? PAGE_SIZE;
  const sortBy = searchParams.sortBy;
  const sortOrder = searchParams.sortOrder;
  const [searchInput, setSearchInput] = React.useState(searchParams.search ?? '');
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = React.useState(false);
  const [highlightedIssueId, setHighlightedIssueId] = React.useState<string | null>(null);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setSearchInput(searchParams.search ?? '');
  }, [searchParams.search]);

  useBreadcrumbs([{ label: 'Home' }, { label: 'Issues' }]);

  const lastSeenRange = React.useMemo(
    () => parseIsoToDateRange(searchParams.lastSeenStart, searchParams.lastSeenEnd),
    [searchParams.lastSeenEnd, searchParams.lastSeenStart]
  );

  const lastSeenIsoRange = React.useMemo(() => toInclusiveIsoRange(lastSeenRange), [lastSeenRange]);
  const exportUrl = React.useMemo(() => {
    const params = new URLSearchParams();
    params.set('format', 'csv');
    if (lastSeenIsoRange.start) params.set('lastSeenStart', lastSeenIsoRange.start);
    if (lastSeenIsoRange.end) params.set('lastSeenEnd', lastSeenIsoRange.end);
    if (severityFilter !== 'all') params.set('severity', severityFilter);
    if (propertyFilter !== 'all') params.set('propertyId', propertyFilter);
    if (statusFilter.length > 0) params.set('statuses', statusFilter.join(','));
    if (categoryFilter.length > 0) params.set('categoryIds', categoryFilter.join(','));
    if (credentialFilter.length > 0) params.set('credentialIds', credentialFilter.join(','));
    if (tagFilter.length > 0) params.set('tagIds', tagFilter.join(','));
    if (showArchived) params.set('includeArchived', 'true');
    if (searchParams.search?.trim()) params.set('search', searchParams.search.trim());
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);
    return `${API_URL}/v2/issues?${params.toString()}`;
  }, [
    lastSeenIsoRange.start,
    lastSeenIsoRange.end,
    severityFilter,
    propertyFilter,
    statusFilter,
    categoryFilter,
    credentialFilter,
    tagFilter,
    showArchived,
    searchParams.search,
    sortBy,
    sortOrder,
  ]);

  const issuesQ = useIssuesV2Query(
    {
      page,
      pageSize,
      severity: severityFilter,
      propertyId: propertyFilter,
      statuses: statusFilter,
      search: searchParams.search?.trim(),
      lastSeenStart: lastSeenIsoRange.start,
      lastSeenEnd: lastSeenIsoRange.end,
      categoryIds: categoryFilter.length > 0 ? categoryFilter : undefined,
      credentialIds: credentialFilter.length > 0 ? credentialFilter : undefined,
      tagIds: tagFilter.length > 0 ? tagFilter : undefined,
      includeArchived: showArchived || undefined,
      sortBy,
      sortOrder,
    },
    { placeholderData: (previous) => previous }
  );

  const handleSortChange = React.useCallback(
    (newSortBy: string | undefined, newSortOrder: 'asc' | 'desc' | undefined) => {
      updateSearch({
        sortBy: newSortBy as IssueSortField | undefined,
        sortOrder: newSortOrder,
        page: 1, // Reset to first page when sorting changes
      });
    },
    [updateSearch]
  );

  const propertiesQ = useQuery({
    queryKey: ['properties'],
    queryFn: async (): Promise<{ items: PropertyItem[] }> => {
      const r = await fetch(API_URL + '/org/properties', { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to fetch properties');
      return r.json();
    },
  });
  const categoriesQ = useIssueCategoriesQuery();
  const credentialsQ = useOrgCredentials();
  const tagsQ = useTagsQuery();
  const { toast } = useToast();
  const bulkArchiveMutation = useBulkArchiveIssues();
  const bulkReproVerifyMutation = useBulkReproVerify();

  const propertyOptions: SelectOption[] = React.useMemo(() => {
    const options: SelectOption[] = [{ id: 'all', label: 'All Applications' }];
    propertiesQ.data?.items.forEach((prop) => {
      options.push({ id: prop.id, label: prop.name });
    });
    return options;
  }, [propertiesQ.data]);

  const propertyNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    propertiesQ.data?.items.forEach((prop) => {
      map[prop.id] = prop.name;
    });
    return map;
  }, [propertiesQ.data]);

  const activeCategories = React.useMemo(() => {
    const categories = categoriesQ.data?.data ?? [];
    return categories
      .filter((category) => category.isActive)
      .sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) {
          return a.sortOrder - b.sortOrder;
        }
        return a.label.localeCompare(b.label);
      });
  }, [categoriesQ.data]);

  const categoryNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    activeCategories.forEach((category) => {
      map[category.id] = category.label;
    });
    return map;
  }, [activeCategories]);

  const activeCategoryIdSet = React.useMemo(
    () => new Set(activeCategories.map((category) => category.id)),
    [activeCategories]
  );

  const credentialNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    credentialsQ.data?.items.forEach((cred) => {
      map[cred.id] = cred.name;
    });
    return map;
  }, [credentialsQ.data?.items]);

  const tagsList = React.useMemo(
    () => (tagsQ.data?.data ?? []) as Array<{ id: string; name: string; color: string }>,
    [tagsQ.data]
  );

  const tagNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    tagsList.forEach((tag) => {
      map[tag.id] = tag.name;
    });
    return map;
  }, [tagsList]);

  React.useEffect(() => {
    if (!categoryFilter || categoryFilter.length === 0) return;
    const filtered = categoryFilter.filter((id) => activeCategoryIdSet.has(id));
    if (filtered.length !== categoryFilter.length) {
      updateSearch({ categoryIds: filtered.length > 0 ? filtered : undefined, page: 1 });
    }
  }, [activeCategoryIdSet, categoryFilter, updateSearch]);

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      if ((searchParams.search ?? '') === trimmed) return;
      updateSearch({ search: trimmed || undefined, page: 1 });
    }, 350);
    return () => window.clearTimeout(handle);
  }, [searchInput, searchParams.search, updateSearch]);

  const issueItems = issuesQ.data?.data ?? [];
  const pagination = issuesQ.data?.pagination;
  const totalItems = pagination?.totalItems ?? issueItems.length;

  const [selectedIssueIds, setSelectedIssueIds] = React.useState<Set<string>>(new Set());
  const [selectionScope, setSelectionScope] = React.useState<'page' | 'all' | null>(null);
  const [deselectedIssueIds, setDeselectedIssueIds] = React.useState<Set<string>>(new Set());
  const [selectAllSnapshot, setSelectAllSnapshot] = React.useState<IssuesFilterSnapshot | null>(
    null
  );

  const normalizedFilters = React.useMemo<IssuesFilterSnapshot>(
    () => ({
      severity: severityFilter !== 'all' ? severityFilter : undefined,
      propertyId: propertyFilter !== 'all' ? propertyFilter : undefined,
      statuses: statusFilter.length > 0 ? [...statusFilter] : undefined,
      search: searchParams.search ? searchParams.search.trim() : undefined,
      lastSeenStart: lastSeenIsoRange.start ?? undefined,
      lastSeenEnd: lastSeenIsoRange.end ?? undefined,
      categoryIds: categoryFilter.length > 0 ? [...categoryFilter] : undefined,
      credentialIds: credentialFilter.length > 0 ? [...credentialFilter] : undefined,
      tagIds: tagFilter.length > 0 ? [...tagFilter] : undefined,
      includeArchived: showArchived ? true : undefined,
    }),
    [
      categoryFilter,
      credentialFilter,
      lastSeenIsoRange.end,
      lastSeenIsoRange.start,
      propertyFilter,
      searchParams.search,
      showArchived,
      severityFilter,
      statusFilter,
      tagFilter,
    ]
  );

  const filtersFingerprint = React.useMemo(
    () =>
      JSON.stringify({
        severity: normalizedFilters.severity ?? 'all',
        propertyId: normalizedFilters.propertyId ?? 'all',
        statuses: normalizedFilters.statuses ?? [],
        search: normalizedFilters.search ?? '',
        lastSeenStart: normalizedFilters.lastSeenStart ?? '',
        lastSeenEnd: normalizedFilters.lastSeenEnd ?? '',
        categoryIds: normalizedFilters.categoryIds
          ? normalizedFilters.categoryIds.slice().sort().join('|')
          : '',
        credentialIds: normalizedFilters.credentialIds
          ? normalizedFilters.credentialIds.slice().sort().join('|')
          : '',
        tagIds: normalizedFilters.tagIds ? normalizedFilters.tagIds.slice().sort().join('|') : '',
        includeArchived: normalizedFilters.includeArchived ? 'true' : 'false',
      }),
    [
      normalizedFilters.categoryIds,
      normalizedFilters.credentialIds,
      normalizedFilters.lastSeenEnd,
      normalizedFilters.lastSeenStart,
      normalizedFilters.propertyId,
      normalizedFilters.search,
      normalizedFilters.includeArchived,
      normalizedFilters.severity,
      normalizedFilters.statuses,
      normalizedFilters.tagIds,
    ]
  );

  const clearSelection = React.useCallback(() => {
    setSelectedIssueIds(new Set());
    setSelectionScope(null);
    setDeselectedIssueIds(new Set());
    setSelectAllSnapshot(null);
  }, []);

  React.useEffect(() => {
    clearSelection();
  }, [clearSelection, filtersFingerprint]);

  const rowSelection = React.useMemo<RowSelectionState>(() => {
    const state: RowSelectionState = {};
    if (selectionScope === 'all') {
      issueItems.forEach((issue) => {
        if (!deselectedIssueIds.has(issue.id)) {
          state[issue.id] = true;
        }
      });
      return state;
    }

    issueItems.forEach((issue) => {
      if (selectedIssueIds.has(issue.id)) {
        state[issue.id] = true;
      }
    });

    return state;
  }, [deselectedIssueIds, issueItems, selectedIssueIds, selectionScope]);

  const deselectedCount = selectionScope === 'all' ? deselectedIssueIds.size : 0;
  const selectedCount =
    selectionScope === 'all' ? Math.max(0, totalItems - deselectedCount) : selectedIssueIds.size;
  const canSelectAllResults =
    selectionScope !== 'all' && selectedCount > 0 && totalItems > selectedCount;

  const handleRowSelectionChange = React.useCallback(
    (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      const base = rowSelection;
      const nextState = typeof updater === 'function' ? updater(base) : updater;

      if (selectionScope === 'all') {
        const nextDeselected = new Set(deselectedIssueIds);
        issueItems.forEach(({ id }) => {
          const wasSelected = Boolean(base[id]);
          const isSelected = Boolean(nextState[id]);
          if (wasSelected === isSelected) return;

          if (isSelected) {
            nextDeselected.delete(id);
          } else {
            nextDeselected.add(id);
          }
        });

        if (totalItems > 0 && nextDeselected.size >= totalItems) {
          clearSelection();
          return;
        }

        setDeselectedIssueIds(nextDeselected);
        return;
      }

      const nextSelected = new Set(selectedIssueIds);
      issueItems.forEach(({ id }) => {
        const isSelected = Boolean(nextState[id]);
        if (isSelected) {
          nextSelected.add(id);
        } else {
          nextSelected.delete(id);
        }
      });

      setSelectedIssueIds(nextSelected);
      setSelectionScope(nextSelected.size > 0 ? 'page' : null);
    },
    [
      clearSelection,
      deselectedIssueIds,
      issueItems,
      rowSelection,
      selectedIssueIds,
      selectionScope,
      totalItems,
    ]
  );

  const handleSelectAllResults = React.useCallback(() => {
    if (!canSelectAllResults) return;
    setSelectionScope('all');
    setSelectedIssueIds(new Set());
    setDeselectedIssueIds(new Set());
    setSelectAllSnapshot(normalizedFilters);
  }, [canSelectAllResults, normalizedFilters]);

  const handleArchiveSelected = React.useCallback(async () => {
    if (selectedCount === 0) return;

    const payload =
      selectionScope === 'all'
        ? {
            selectAll: true as const,
            filters: selectAllSnapshot ?? normalizedFilters,
            excludeIssueIds:
              deselectedIssueIds.size > 0 ? Array.from(deselectedIssueIds) : undefined,
          }
        : {
            issueIds: Array.from(selectedIssueIds),
          };

    try {
      const result = await bulkArchiveMutation.mutateAsync(payload);
      clearSelection();
      await issuesQ.refetch();
      toast.success(
        result.updatedCount === 1 ? 'Archived 1 issue' : `Archived ${result.updatedCount} issues`
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to archive selected issues');
    }
  }, [
    bulkArchiveMutation,
    clearSelection,
    deselectedIssueIds,
    issuesQ,
    normalizedFilters,
    selectAllSnapshot,
    selectedCount,
    selectedIssueIds,
    selectionScope,
    toast,
  ]);

  const handleRevalidateSelected = React.useCallback(async () => {
    if (selectedCount === 0) return;

    const payload =
      selectionScope === 'all'
        ? {
            selectAll: true as const,
            filters: selectAllSnapshot ?? normalizedFilters,
            excludeIssueIds:
              deselectedIssueIds.size > 0 ? Array.from(deselectedIssueIds) : undefined,
          }
        : {
            issueIds: Array.from(selectedIssueIds),
          };

    try {
      const result = await bulkReproVerifyMutation.mutateAsync(payload);
      clearSelection();
      const parts: string[] = [];
      if (result.enqueuedCount > 0) {
        parts.push(
          `Queued ${result.enqueuedCount} ${result.enqueuedCount === 1 ? 'issue' : 'issues'} for verification`
        );
      }
      if (result.skippedCount > 0) {
        parts.push(`${result.skippedCount} skipped without repro steps`);
      }
      if (parts.length > 0) {
        if (result.enqueuedCount > 0) {
          toast.success(parts.join(', '));
        } else {
          toast.warning(parts.join(', '));
        }
      } else {
        toast.info('No issues matched for verification');
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to queue issues for verification'
      );
    }
  }, [
    bulkReproVerifyMutation,
    clearSelection,
    deselectedIssueIds,
    normalizedFilters,
    selectAllSnapshot,
    selectedCount,
    selectedIssueIds,
    selectionScope,
    toast,
  ]);

  const resolvedSelectionMode: 'page' | 'all' = selectionScope === 'all' ? 'all' : 'page';

  const bulkSelectionConfig =
    selectedCount > 0
      ? {
          selectedCount,
          totalItems,
          selectionMode: resolvedSelectionMode,
          deselectedCount: selectionScope === 'all' ? deselectedCount : undefined,
          canSelectAllResults,
          onSelectAllResults: canSelectAllResults ? handleSelectAllResults : undefined,
          onClearSelection: clearSelection,
          onArchiveSelected: handleArchiveSelected,
          isArchiveLoading: bulkArchiveMutation.isPending,
          onRevalidateSelected: handleRevalidateSelected,
          isRevalidateLoading: bulkReproVerifyMutation.isPending,
        }
      : undefined;

  const selectedIssueId = searchParams.issueId ?? null;
  const selectedIssueIndex = React.useMemo(() => {
    if (!selectedIssueId) return -1;
    return issueItems.findIndex((issue) => issue.id === selectedIssueId);
  }, [selectedIssueId, issueItems]);

  const _selectedIssue: IssueSummaryV2 | undefined =
    selectedIssueIndex >= 0 ? issueItems[selectedIssueIndex] : undefined;

  React.useEffect(() => {
    setDrawerOpen(Boolean(selectedIssueId));
  }, [selectedIssueId]);

  const handleDrawerOpenChange = React.useCallback(
    (isOpen: boolean) => {
      setDrawerOpen(isOpen);
      if (isOpen) {
        if (!selectedIssueId && issueItems.length > 0) {
          updateSearch({ issueId: issueItems[0].id });
        }
      } else {
        updateSearch({ issueId: undefined });
      }
    },
    [selectedIssueId, issueItems, updateSearch]
  );

  const navigateToIssue = React.useCallback(
    (index: number) => {
      if (issueItems.length === 0) return;

      let newIndex = index;
      if (newIndex < 0) newIndex = issueItems.length - 1;
      if (newIndex >= issueItems.length) newIndex = 0;

      const issue = issueItems[newIndex];
      if (issue) {
        setHighlightedIssueId(issue.id);
        updateSearch({ issueId: issue.id });
      }
    },
    [issueItems, updateSearch]
  );

  const navigateNext = React.useCallback(() => {
    navigateToIssue(selectedIssueIndex + 1);
  }, [navigateToIssue, selectedIssueIndex]);

  const navigatePrev = React.useCallback(() => {
    navigateToIssue(selectedIssueIndex - 1);
  }, [navigateToIssue, selectedIssueIndex]);

  React.useEffect(() => {
    if (!drawerOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't navigate when focused in text inputs
      const activeEl = document.activeElement;
      const isTextInput =
        activeEl instanceof HTMLTextAreaElement ||
        activeEl instanceof HTMLInputElement ||
        activeEl?.getAttribute('contenteditable') === 'true';

      if (isTextInput) return;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigatePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [drawerOpen, navigateNext, navigatePrev]);

  // Clear highlight when clicking outside table (and outside drawer/overlays)
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Don't clear if clicking inside table, drawer, or any overlay
      if (
        tableContainerRef.current?.contains(target) ||
        target.closest('[role="dialog"]') ||
        target.closest('[data-radix-popper-content-wrapper]')
      ) {
        return;
      }
      setHighlightedIssueId(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusUpdated = React.useCallback(() => {
    issuesQ.refetch();
  }, [issuesQ]);

  const handleLastSeenChange = React.useCallback(
    (range: DateRangeValue) => {
      const iso = toInclusiveIsoRange(range);
      updateSearch({
        lastSeenStart: iso.start,
        lastSeenEnd: iso.end,
        page: 1,
      });
    },
    [updateSearch]
  );

  const handleClearLastSeen = React.useCallback(() => {
    updateSearch({ lastSeenStart: undefined, lastSeenEnd: undefined, page: 1 });
  }, [updateSearch]);

  const handleCategoryToggle = React.useCallback(
    (categoryId: string, nextSelected: boolean) => {
      const next = new Set(categoryFilter);
      if (nextSelected) {
        next.add(categoryId);
      } else {
        next.delete(categoryId);
      }
      const updated = Array.from(next);
      updateSearch({
        categoryIds: updated.length > 0 ? updated : undefined,
        page: 1,
      });
    },
    [categoryFilter, updateSearch]
  );

  const credentialsList = React.useMemo(
    () => credentialsQ.data?.items ?? [],
    [credentialsQ.data?.items]
  );

  const {
    filterDialogTrigger,
    activeFilterChips,
    hasActiveFilters,
    filtersCount,
    handleFilterRemove,
    handleResetFilters,
  } = IssuesFilters({
    statusFilter,
    severityFilter,
    propertyFilter,
    categoryFilter,
    credentialFilter,
    tagFilter,
    showArchived,
    lastSeenRange,
    propertyOptions,
    propertyNameMap,
    categoryNameMap,
    credentials: credentialsList,
    credentialNameMap,
    tags: tagsList,
    tagNameMap,
    credentialsLoading: credentialsQ.isLoading,
    tagsLoading: tagsQ.isLoading,
    onFilterChange: updateSearch,
    onLastSeenChange: handleLastSeenChange,
    onClearLastSeen: handleClearLastSeen,
    onCategoryToggle: handleCategoryToggle,
    isOpen: isFilterDialogOpen,
    onOpenChange: setIsFilterDialogOpen,
  });

  const categoryOptions = React.useMemo(
    () => activeCategories.map((category) => ({ id: category.id, label: category.label })),
    [activeCategories]
  );

  // Fuse the toolbar with the table card; stand alone over the loading and
  // empty states (which carry their own framing).
  const isTableLockup = !issuesQ.isLoading && issueItems.length > 0;

  const resultsLabel = React.useMemo(() => {
    const resultsWord = totalItems === 1 ? 'result' : 'results';
    if (hasActiveFilters) {
      const filtersWord = filtersCount === 1 ? 'filter' : 'filters';
      return `${totalItems} ${resultsWord} · ${filtersCount} ${filtersWord}`;
    }
    return `${totalItems} ${resultsWord}`;
  }, [filtersCount, hasActiveFilters, totalItems]);

  const handleCopyTriagePrompt = async () => {
    const prompt = buildTriagePrompt({
      statuses: statusFilter,
      severity: severityFilter,
      tagNames: tagFilter.map((id) => tagNameMap[id]).filter((name): name is string => Boolean(name)),
      applicationId: propertyFilter,
      search: searchParams.search,
    });
    try {
      await navigator.clipboard.writeText(prompt);
      toast.success('Triage prompt copied');
    } catch {
      toast.error('Failed to copy triage prompt');
    }
  };

  return (
    <DocsAnnotation slug="reference/issues" title="Issues">
      <Page>
        <PageHeader
          icon={<Warning size={24} />}
          title="Issues"
          description="All detected issues across your crawled pages"
        />

        {/* Results count + export sit outside the lockup, right-aligned above
            it; the toolbar below fuses with the table card (mirrors /flows) */}
        <div className="mt-6">
          <Flex align="center" justify="end" gap="sm" className="mb-2">
            <Text size="sm" className="text-muted-foreground whitespace-nowrap">
              {resultsLabel}
            </Text>
            <Button variant="outline" size="sm" iconLeft={<Copy size={16} />} onPress={handleCopyTriagePrompt}>
              Copy Triage Prompt
            </Button>
            <Button
              variant="icon"
              size="sm"
              iconOnly
              tooltip="Export CSV"
              onPress={() => window.open(exportUrl, '_blank')}
            >
              <Download size={16} />
            </Button>
          </Flex>

          <IssuesFilterBar
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            statusFilter={statusFilter}
            severityFilter={severityFilter}
            categoryFilter={categoryFilter}
            categoryOptions={categoryOptions}
            categoriesLoading={categoriesQ.isLoading}
            onFilterChange={updateSearch}
            filterDialogTrigger={filterDialogTrigger}
            activeFilterChips={activeFilterChips}
            onChipRemove={handleFilterRemove}
            onClearAll={() => {
              setSearchInput('');
              handleResetFilters();
            }}
            showClearAll={hasActiveFilters || searchInput.length > 0}
            attached={isTableLockup}
          />

          {issuesQ.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingOrbit size="lg" />
            </div>
          ) : (
            <div ref={tableContainerRef} className={isTableLockup ? undefined : 'mt-3'}>
              <IssuesTableV2
                tableContainerClassName={
                  isTableLockup ? 'rounded-t-none filter-lockup-table' : undefined
                }
                issues={issueItems}
                enableSelection
                rowSelection={rowSelection}
                onRowSelectionChange={handleRowSelectionChange}
                bulkSelection={bulkSelectionConfig}
                pagination={
                  pagination
                    ? {
                        page,
                        pageSize: pagination.pageSize,
                        totalItems: pagination.totalItems,
                        totalPages: pagination.totalPages,
                        onPageChange: (nextPage) => updateSearch({ page: nextPage }),
                        isLoading: issuesQ.isFetching,
                      }
                    : undefined
                }
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                onIssueClick={(issue) => {
                  setHighlightedIssueId(issue.id);
                  updateSearch({ issueId: issue.id });
                }}
                highlightedIssueId={highlightedIssueId}
                emptyState={
                  totalItems === 0 ? (
                    <EmptyState
                      graphic={<EmptyStateScanGraphic />}
                      title="No open issues"
                      description="Nothing detected across your crawled pages — nice job. New findings will land here as runs analyze your app."
                    />
                  ) : undefined
                }
                emptyMessage={
                  <>
                    <Warning size={48} className="mx-auto text-muted-foreground mb-4" />
                    <Text className="text-muted-foreground">No issues on this page</Text>
                    <Text size="sm" className="text-muted-foreground mt-1">
                      Try adjusting filters or pagination.
                    </Text>
                  </>
                }
              />
            </div>
          )}
        </div>

        <DrawerTrigger isOpen={drawerOpen} onOpenChange={handleDrawerOpenChange}>
          <button style={{ display: 'none' }} />
          <Drawer hideCloseButton>
            {selectedIssueId ? (
              <IssueDrawerV2
                issueId={selectedIssueId}
                pageIndex={selectedIssueIndex >= 0 ? selectedIssueIndex : 0}
                totalItems={issueItems.length}
                canNavigate={issueItems.length > 1 && selectedIssueIndex >= 0}
                onNavigateNext={navigateNext}
                onNavigatePrev={navigatePrev}
                onStatusUpdated={handleStatusUpdated}
                onClose={() => handleDrawerOpenChange(false)}
                categories={activeCategories}
              />
            ) : null}
          </Drawer>
        </DrawerTrigger>
      </Page>
    </DocsAnnotation>
  );
}
